/**
 * Shared builder: PL `PersonalisedProductsWidgetData` (the personalised homepage swimlane) →
 * domain `CustomSection`.
 *
 * Mirrors `featured-products-showcase-section-builders.ts`: dependency-light, no JSX, so the widget
 * can render the existing `PersonalisedProducts` section chrome (DS + legacy routing + product-card
 * analytics) without duplicating the proto→domain conversion.
 *
 * Unlike the Contentful-driven flow, the feed is resolved at PL serve time (productFeedType /
 * productFeedLimit → RECOMMENDATION datasource) and arrives enriched as PRODUCT_CARD children —
 * so `fetchProductsFromAPI` is pinned false and the section never re-fetches client-side.
 */

import type { IProductCard } from '@src/components/product-card/product-card.types'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'

import { makeSection } from '../banner-section-builders'
import { buildWidgetTrackingMetaData, protoButtonToDomain, toRichText } from '../widget-utils'

import type { PersonalisedProductsWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

export function buildPersonalisedProductsConfig(
  data: PersonalisedProductsWidgetData,
  productCards: IProductCard[],
): CustomSection {
  const heading = toRichText(data.heading as Parameters<typeof toRichText>[0])
  return makeSection({
    id: data.id,
    sectionType: data.sectionType as CUSTOM_SECTION_TYPE,
    richTexts: (heading ? [heading] : []) as CustomSection['richTexts'],
    buttons: data.buttons.map(protoButtonToDomain) as CustomSection['buttons'],
    productCards,
    // The PL feed is server-enriched; the section's client-side feed fetch must stay off.
    fetchProductsFromAPI: false,
    numberOfProductsToShowInDesktop: data.numberOfProductsToShowInDesktop,
    numberOfProductsToShowInMobile: data.numberOfProductsToShowInMobile,
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics),
    classNames: data.classNames,
    classNamesMobileOverrides: data.classNamesMobileOverrides,
    baseDynamicClasses: data.baseDynamicClasses,
    mobileDynamicClassesOverrides: data.mobileDynamicClassesOverrides,
  })
}
